/* eslint-disable max-lines */
import { Alert, Col, Row, Tabs, Typography } from 'antd';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Trans } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { BackButton } from 'src/components/BackButton/BackButton';
import { BasePage } from 'src/components/BasePage';
import { Button } from 'src/components/Button/Button';
import { Empty } from 'src/components/Empty/Empty';
import { NotFound } from 'src/components/Errors/NotFound';
import { LoanApplicationStatus } from 'src/components/LoanApplicationStatus/LoanApplicationStatus';
import { SpinnerFullWidth } from 'src/components/Spinner/SpinnerFullWidth';
import { EntityLinks } from 'src/core/router/EntityLinks';
import { LoanApplicationDto } from 'src/generated-api-client';
import { ApplicationDetailsGeneralInformationTab } from 'src/pages/ApplicationDetailsPage/ApplicationDetailsGeneralInformationTab';
import { ApplicationDetailsRepaymentsTab } from 'src/pages/ApplicationDetailsPage/ApplicationDetailsRepaymentsTab';
import { ApplicationsStore } from 'src/stores/ApplicationsStore/ApplicationsStore';
import { ApplicationStatus } from 'src/stores/ApplicationsStore/ApplicationsStore.types';
import { UrlHelper } from 'src/utils/UrlHelper';
import styled from 'styled-components';

export const ApplicationDetailsPage = observer(() => {
    const { id } = useParams<{ id: string }>();

    useEffect(() => {
        if (!id) {
            return;
        }

        ApplicationsStore.loadItem(id);

        return () => {
            ApplicationsStore.itemLoader.reset();
        };
    }, [id]);

    const { itemLoader } = ApplicationsStore;
    const application =
        ApplicationsStore.currentItem as LoanApplicationDto | null;

    if (itemLoader.isLoading) {
        return <SpinnerFullWidth />;
    }

    if (itemLoader.errorData?.status === 404) {
        return <NotFound />;
    }

    const renderContent = () => {
        if (itemLoader.hasError) {
            return (
                <Typography.Text type="danger">
                    {t(
                        'Page.ApplicationDetailsPage.LoadError',
                        'An error occurred while loading application data',
                    )}
                </Typography.Text>
            );
        }

        if (!application) {
            return (
                <Empty
                    description={t(
                        'Page.ApplicationDetailsPage.Empty',
                        'Application data is not available',
                    )}
                />
            );
        }

        return <Tabs items={tabsItems} />;
    };

    const renderAlert = () => {
        if (application?.status === ApplicationStatus.PENDING_ASSIGNMENT) {
            return (
                <StyledAlert
                    message={t(
                        'Page.ApplicationDetailsPage.Alert.PendingAssignment',
                        'Congratulations! Please wait for the assignment of the partner.',
                    )}
                    type="success"
                />
            );
        }
        if (application?.status === ApplicationStatus.ASSIGNED) {
            return (
                <StyledAlert
                    message={t(
                        'Page.ApplicationDetailsPage.Alert.Assigned',
                        'Congratulations! Your financing partner is ready! Tap to continue your business growth journey.',
                    )}
                    type="success"
                />
            );
        }
        if (application?.status === ApplicationStatus.DECLINED) {
            return (
                <StyledAlert
                    message={t(
                        'Page.ApplicationDetailsPage.Alert.Declined',
                        'Decline reason: {reason}',
                        { reason: application?.finalDeclineReason },
                    )}
                    type="error"
                />
            );
        }

        return null;
    };

    const showRepaymentsTab =
        application?.status === ApplicationStatus.ACTIVE ||
        application?.status === ApplicationStatus.FULLY_PAID;

    const tabsItems = [
        {
            key: 'generalInformation',
            label: (
                <Trans
                    i18nKey="Page.ApplicationDetailsPage.Tabs.GeneralInformation"
                    defaults="General information"
                />
            ),
            children: <ApplicationDetailsGeneralInformationTab />,
        },
        {
            key: 'repayments',
            disabled: !showRepaymentsTab,
            label: (
                <Trans
                    i18nKey="Page.ApplicationDetailsPage.Tabs.Repayments"
                    defaults="Repayments"
                />
            ),
            children: <ApplicationDetailsRepaymentsTab />,
        },
    ].filter((tab) => !tab.disabled);

    return (
        <BasePage
            pageName="ApplicationDetailsPage"
            isLoading={itemLoader.isLoading}
        >
            <div style={{ marginBottom: 24 }}>
                <BackButton
                    to={
                        EntityLinks.loanApplications.list?.() ?? '/applications'
                    }
                >
                    {t(
                        'Page.ApplicationDetailsPage.Back',
                        'Back to applications',
                    )}
                </BackButton>
            </div>
            <Row gutter={[16, 16]}>
                <Col flex={1}>
                    <Typography.Title level={3}>
                        {application?.number}
                    </Typography.Title>
                </Col>
                <Col>
                    <StyledExtra>
                        <LoanApplicationStatus status={application?.status} />
                    </StyledExtra>
                </Col>
            </Row>
            {renderAlert()}
            {application?.status === ApplicationStatus.ASSIGNED && (
                <StyledContinueToPartnerJourneyWrapper>
                    <Button
                        type="primary"
                        onClick={() => {
                            if (application?.partnerSystemLink) {
                                UrlHelper.openUrlInNewTab(
                                    application?.partnerSystemLink,
                                );
                            }
                        }}
                    >
                        {t(
                            'Page.ApplicationDetailsPage.ContinueToPartnerJourney',
                            'Continue to partner journey',
                        )}
                    </Button>
                </StyledContinueToPartnerJourneyWrapper>
            )}
            {renderContent()}
        </BasePage>
    );
});

const StyledAlert = styled(Alert)`
    margin-top: 16px;
    margin-bottom: 16px;
`;

const StyledExtra = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const StyledContinueToPartnerJourneyWrapper = styled.div`
    position: relative;
    margin-top: 16px;
    margin-bottom: 16px;
`;
